import { Tab } from '@headlessui/react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { RootState } from '$store';
import {
  DatePicker,
  HiddenInput,
  Input,
  MultipleSelect,
  Select,
  TagInput,
  Textarea,
} from '~components/generic/elements';
import Loader from '~components/generic/loader/Loader';
import useAppDispatch from '~hooks/useAppDispatch';
import useAssets from '~hooks/useAssets';
import useMediaLibrarySettings from '~modules/media-library/context/useMediaLibrarySettings';
import {
  addRelationThunk,
  fetchContributionAsset,
  saveAssetData,
  saveContributionDraft,
} from '~modules/media-library/controller/thunks/contribute';
import { TagInput as FreeTagsInput } from '~modules/media-library/view/Contribute/EditAssetsForm/TagInput';
import RelevantDocuments from '~modules/media-library/view/Contribute/RelevantDocuments/RelevantDocuments';
import { IValueOptionsShape } from '~services/entity-definitions/interface';
import styles from './EditAssetsForm.module.css';

export interface UploadType {
  id: string;
  label: string;
  assetType: string;
  allowedFormats: string[];
  relationType: string;
}

export interface UploadTypes {
  [key: string]: UploadType;
}

interface EditAssetsFormProps {
  assetId?: string;
  onStepComplete?: () => void;
}

interface FieldError {
  errors?: string[];
}

interface FormErrors {
  [key: string]: FieldError;
}

interface FieldSchema {
  attr?: {
    tab?: string;
  };
}

export function EditAssetsForm({ assetId, onStepComplete }: EditAssetsFormProps) {
  const [loading, setLoading] = useState(false);
  const contribute = useSelector((store: RootState | any) => store.contribute);
  const { contributionMode, contributionDataLoading, selectedFields } = contribute;
  const { getAssetById } = useAssets();
  const asset = getAssetById(assetId);
  const [activeTab, setActiveTab] = useState(0);
  const contributionTypeChanged = contribute.fileSelected.contributionType;
  let type = contributionTypeChanged ? contributionTypeChanged : asset.type.toLocaleLowerCase();

  const dispatch = useAppDispatch();

  const { additionalSettings } = useMediaLibrarySettings();

  // Retrieve document types for the selected contribution Type
  const additionalUploadTypes: UploadTypes =
    additionalSettings.contribution_types[contribute.fileSelected.contributionType]?.additional_upload_types;

  useEffect(() => {
    if (!assetId) return;

    const workflowStatus = asset ? asset.attributes.workflowStatus : null;

    dispatch(fetchContributionAsset({ assetId, contributionType: type, workflowStatus: workflowStatus }));
  }, [dispatch, assetId, type, asset]);

  const mergedContributionData = Object.keys(contribute.contributionDraftData).reduce((acc, key) => {
    if (selectedFields && selectedFields[key] !== undefined) {
      acc[key] = selectedFields[key];
    } else {
      acc[key] = contribute.contributionDraftData[key];
    }
    return acc;
  }, {});

  const contributionData =
    contributionMode !== 'edit-single' ? mergedContributionData : contribute.contributionDraftData;

  interface FormValues {
    [key: string]: string;
  }

  const fieldsByTab =
    contribute.contributionDraftSchema.properties &&
    Object.entries(contribute?.contributionDraftSchema?.properties).reduce(
      (
        acc: { [key: string]: { fieldName: string; fieldSchema: FieldSchema }[] },
        [fieldName, fieldSchema]: [string, FieldSchema],
      ) => {
        const tab = fieldSchema.attr?.tab;
        if (tab) {
          if (!acc[tab]) acc[tab] = [];
          acc[tab].push({ fieldName, fieldSchema });
        }
        return acc;
      },
      {},
    );

  // Add the "Relevant Documents" tab only when there are additional upload types
  if (
    fieldsByTab &&
    additionalUploadTypes &&
    Object.keys(additionalUploadTypes).length > 0 &&
    !fieldsByTab['Relevant Documents']
  ) {
    fieldsByTab['Relevant Documents'] = [];
  }

  const tabs = fieldsByTab && Object.keys(fieldsByTab);

  const tabIndexes =
    tabs &&
    tabs.reduce((acc, tab, index) => {
      acc[tab] = index;
      return acc;
    }, {});

  const handleErrors = () => {
    const errorTabIndexes = tabs
      .filter(tab => fieldsByTab[tab].some(({ fieldName }) => errors[fieldName]))
      .map(tab => tabIndexes[tab]);
    setTabsWithErrors(errorTabIndexes);
  };

  const {
    control,
    handleSubmit,
    setError,
    watch,
    getValues,
    formState: { errors },
    reset,
    trigger,
  } = useForm<FormValues>({
    defaultValues: {},
    mode: 'onSubmit',
  });

  // Track which tab has errors
  const [tabsWithErrors, setTabsWithErrors] = useState<number[]>([]);

  useEffect(() => {
    if (!contributionDataLoading) {
      reset(contributionData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, contributionDataLoading]);

  useEffect(() => {
    if (!contribute.onSaveContributionDraftResponse.errors) {
      return;
    }

    const formErrors = contribute.onSaveContributionDraftResponse.errors.children as FormErrors;

    Object.entries(formErrors).forEach(([field, fieldErrors]) => {
      if ('errors' in fieldErrors && fieldErrors.errors && fieldErrors.errors.length > 0) {
        setError(field, { type: 'server', message: fieldErrors.errors[0] });
        handleErrors();
        // if errors reset the active tab to Info
        setActiveTab(0);
      }
    });
    // eslint-disable-next-line
  }, [contribute.onSaveContributionDraftResponse.errors, setError]);

  const onContribute = async (data: FormValues) => {
    sendForm('contribute', data);
  };

  const onSaveAsDraft = async () => {
    sendForm('draft', getValues());
  };

  const onSave = async (data: FormValues) => {
    sendForm('save', data);
  };

  const sendForm = async (action: string, data: FormValues) => {
    setLoading(true);

    // Add asset relations from the previous selection
    if (selectedFields.relevantDocuments) {
      dispatch(addRelationThunk({ files: selectedFields.relevantDocuments, assetId }));
    }

    // Because backend requires FreeTags and other dynamic_keyword_list fields as string,
    // for such fields convert array of strings to a single string
    Object.keys(data).forEach(key => {
      if (Array.isArray(data[key]) && contribute?.contributionDraftSchema?.properties[key]?.type === 'string') {
        data[key] = data[key].join(', ');
      }
    });

    if ('draft' === action || 'contribute' === action) {
      let updatedData = {};
      if ('contribute' === action) updatedData = { ...data, Contribute: 'save' };
      else updatedData = { ...data, Save: 'save' };

      const res: any = await dispatch(
        saveContributionDraft({
          data: updatedData,
          assetId: assetId,
          contributionType: type,
        }),
      );

      if (res && !res?.payload?.requestResponse?.errors && !res.payload.error) {
        onStepComplete();
      } else if (res?.payload?.error) {
        setError('serverError', { type: 'server', message: res.payload.error });
        // if errors reset the active tab to Info & setLoading false
        setActiveTab(0);
        setLoading(false);
      }
    } else {
      const res: any = await dispatch(
        saveAssetData({
          data: data,
          assetId: assetId,
          contributionType: type,
        }),
      );
      if (!res.payload.errors) {
        onStepComplete();
      } else if (res?.payload?.error) {
        setError('serverError', { type: 'server', message: res.payload.error });
        // if errors reset the active tab to Info & setLoading false
        setActiveTab(0);
        setLoading(false);
      }
    }

    setLoading(false);
  };

  if (loading || contributionDataLoading) {
    return (
      <div style={{ minHeight: '690px' }} className="flex justify-center">
        <Loader />
      </div>
    );
  }

  const getEnumTitle = (value, enumValues, enumTitles) => {
    const index = enumValues.indexOf(value);
    return index !== -1 ? enumTitles[index] : null;
  };

  const evaluateDisplayConditions = (conditions, formValues) => {
    const conditionPairs = conditions.split(';');
    return conditionPairs.every(condition => {
      const [field, value] = condition.split('=');
      const fieldTitle = getEnumTitle(
        formValues[field],
        contribute.contributionDraftSchema.properties[field].enum,
        contribute.contributionDraftSchema.properties[field].enum_titles,
      );
      return fieldTitle === value;
    });
  };

  const renderField = (fieldName, fieldSchema) => {
    const { type, widget, title, description, display_conditions } = fieldSchema;
    let options, enum_titles;
    let inputType = 'text';
    if (widget === 'textarea') inputType = 'textarea';
    if (widget === 'email') inputType = 'email';
    if (widget === 'time') inputType = 'datepicker';
    if (widget === 'dynamic_keyword_list') inputType = 'taginput';
    if (fieldSchema.enum) {
      inputType = 'select';
      options = fieldSchema.enum;
      enum_titles = fieldSchema.enum_titles;
    }
    if (type === 'array') {
      inputType = 'multipleselect';
      options = fieldSchema.items.enum;
      enum_titles = fieldSchema.items.enum_titles;
    }
    if (type === 'submit') return null;

    if (display_conditions) {
      const conditionFields = display_conditions.split(';').map(condition => condition.split('=')[0]);
      const watchedValues = watch(conditionFields);

      const formValues = getValues();
      const combinedValues = Object.assign({}, formValues, watchedValues);

      if (!evaluateDisplayConditions(display_conditions, combinedValues)) {
        return null;
      }
    }

    const isRequired = contribute.contributionDraftSchema?.required.includes(fieldName);
    const defaultValue = contributionMode === 'new' ? '' : contributionData?.[fieldName];
    const inputClassName = `max-h-3 mt-3 block w-full resize-none py-3 px-3 text-sm/6 `;
    const inputDateClassName = `mt-3 block resize-none py-1.5 px-3 text-sm/6 `;
    const textAreaClassName = `mt-3 block w-full resize-none py-1 px-3 text-sm/6 `;
    const selectClassName = `mt-3 py-1.5 px-3 `;

    return (
      <div key={fieldName} className="mb-4 ">
        <label htmlFor={fieldName} className="block text-sm font-medium text-gray-700">
          {title}
          {isRequired && <span> *</span>}
        </label>
        <Controller
          name={fieldName}
          control={control}
          defaultValue={defaultValue}
          rules={{ required: isRequired ? `${title} is required` : false }}
          render={({ field }) => {
            switch (inputType) {
              case 'textarea':
                return (
                  <Textarea
                    name={fieldName}
                    value={defaultValue !== undefined ? String(defaultValue) : undefined}
                    onChange={(value: string) => field.onChange(value)}
                    required={isRequired}
                    className={textAreaClassName}
                  />
                );
              case 'select':
                return (
                  <Select
                    name={fieldName}
                    value={defaultValue !== undefined ? String(defaultValue) : undefined}
                    options={getSelectOptions(options, enum_titles)}
                    onChange={(value: string | number) => field.onChange(value)}
                    placeholder={'Select an option'}
                    required={isRequired}
                    className={selectClassName}
                  />
                );
              case 'multipleselect':
                return (
                  <MultipleSelect
                    name={fieldName}
                    defaultValue={defaultValue}
                    options={getSelectOptions(options, enum_titles)}
                    onChange={(value: string[] | number[]) => field.onChange(value)}
                  />
                );
              case 'taginput':
                if (fieldName === 'FreeTags') {
                  const freeTags = contributionData?.FreeTags || [];

                  return (
                    <Controller
                      name={fieldName}
                      control={control}
                      defaultValue={freeTags}
                      render={({ field }) => (
                        <FreeTagsInput
                          {...field}
                          tags={field.value || freeTags}
                          onChange={tags => {
                            field.onChange(tags);
                          }}
                        />
                      )}
                    />
                  );
                }
                return (
                  <TagInput
                    name={fieldName}
                    defaultValue={defaultValue !== undefined ? String(defaultValue) : undefined}
                    onChange={(value: string) => field.onChange(value)}
                    required={isRequired}
                  />
                );
              case 'datepicker':
                return (
                  <DatePicker
                    name={fieldName}
                    defaultValue={defaultValue !== undefined ? String(defaultValue) : undefined}
                    onChange={(value: string) => field.onChange(value)}
                    required={isRequired}
                    className={inputDateClassName}
                  />
                );
              case 'hidden':
                return <HiddenInput name={fieldName} value={defaultValue} />;
              case 'email':
                return (
                  <Input
                    type={'email'}
                    name={fieldName}
                    value={defaultValue !== undefined ? String(defaultValue) : undefined}
                    onChange={(value: string) => field.onChange(value)}
                    required={isRequired}
                    className={inputClassName}
                  />
                );
              default:
                return (
                  <Input
                    type={'text'}
                    name={fieldName}
                    value={defaultValue !== undefined ? String(defaultValue) : undefined}
                    onChange={(value: string) => field.onChange(value)}
                    required={isRequired}
                    className={inputClassName}
                  />
                );
            }
          }}
        />
        {errors[fieldName] && <p className={styles.requried}>{errors[fieldName].message}</p>}
        {description && <small className="text-gray-500">{description}</small>}
      </div>
    );
  };

  const getSelectOptions = (optionValues: string[] | number[], optionLabels: string[]): IValueOptionsShape => {
    if (optionValues) {
      return optionValues.map((value, index) => {
        return { label: optionLabels ? optionLabels[index] : value, value };
      });
    }
    return [];
  };

  const draft = asset && 'draft' === asset.attributes.workflowStatus;

  const handleContinue = async () => {
    const currentTabFields = fieldsByTab[tabs[activeTab]].map(({ fieldName }) => fieldName);
    const isValid = await trigger(currentTabFields);

    if (isValid) {
      setActiveTab(prev => prev + 1);
    }
  };

  const handleTabClick = async (index: number) => {
    // If all previous tabs are valid, set the active tab to the selected tab
    setActiveTab(index);
  };

  return (
    <form>
      <h3>{contributionMode === 'edit-single' || contributionMode === 'edit-multi' ? 'Edit' : 'Create'} Metadata</h3>
      <Tab.Group selectedIndex={activeTab}>
        <Tab.List className="border-t-0 border-l-0 border-r-0 border-b border-solid border-darkgray flex gap-6">
          {tabs.map((tab, index) => (
            <Tab
              key={tab}
              className={() =>
                clsx(
                  'appearance-none border-t-0 border-l-0 border-r-0 border-b-2',
                  'border-solid outline-none font-sans text-sm text-black bg-transparent m-0 px-0 pb-1 cursor-pointer',
                  activeTab === index ? 'border-black font-semibold' : 'border-transparent',
                  tabsWithErrors.includes(index) ? styles.requried : '',
                )
              }
              onClick={() => handleTabClick(index)}
            >
              {tab}
            </Tab>
          ))}
        </Tab.List>
        <div className="mt-4">
          {Object.entries(fieldsByTab).map(([tab, fields]: any, index: number) => (
            <>
              <div
                key="relevant-documents-{tab}"
                className={`${styles.tabContent} ${activeTab === index ? styles.active : ''}`}
              >
                {fields.map(({ fieldName, fieldSchema }) => renderField(fieldName, fieldSchema))}
                {!additionalUploadTypes ||
                  (tab === 'Relevant Documents' && (
                    <RelevantDocuments
                      selectedRelevantDocuments={selectedFields.relevantDocuments}
                      additionalUploadTypes={additionalUploadTypes}
                      assetId={assetId}
                    />
                  ))}
              </div>
            </>
          ))}
          {errors['serverError'] && <p className={styles.requried}>{errors['serverError'].message}</p>}
        </div>
      </Tab.Group>

      {draft && (
        <div className="flex my-4 justify-left">
          <button
            type="button"
            className="button c-button c-button--light u-padding-horizontal-lg mr-2"
            onClick={e => {
              e.stopPropagation();
              onSaveAsDraft();
            }}
          >
            Save draft
          </button>
          {activeTab < tabs.length - 1 && (
            <button
              type="button"
              className="button c-button c-button--dark u-padding-horizontal-lg mx-2"
              onClick={handleContinue}
            >
              Continue
            </button>
          )}
          {activeTab === tabs.length - 1 && (
            <button
              type="button"
              className="button c-button c-button--dark u-padding-horizontal-lg mx-2"
              onClick={e => {
                e.stopPropagation();
                handleSubmit(onContribute, handleErrors)();
              }}
            >
              Send for approval
            </button>
          )}
        </div>
      )}

      {!draft && (
        <div className="flex my-4 justify-left">
          <button
            type="button"
            className="h-10 button c-button c-button--light u-padding-horizontal-lg mx-2"
            onClick={e => {
              e.stopPropagation();
              handleSubmit(onSave, handleErrors)();
            }}
          >
            Save
          </button>
        </div>
      )}
    </form>
  );
}
